import { Link, useNavigate } from "react-router-dom";
import { LogOut, Copy, LayoutDashboard, ListChecks, CalendarDays, Users, ClipboardList, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Brand from "./Brand";

export default function TopBar() {
  const { user, restaurant, logout } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.primary_role === "Manager";

  const copyCode = () => {
    navigator.clipboard.writeText(restaurant?.join_code || "");
    toast.success("Join code copied");
  };

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-[#E4DDD3]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-3">
        <Link to={isManager ? "/manager" : "/app"} className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          {restaurant?.logo_path ? (
            <img
              src={`/api/files/${restaurant.logo_path}`}
              alt={restaurant.name}
              className="h-9 w-auto max-w-[150px] object-contain rounded-lg"
              data-testid="business-logo"
            />
          ) : (
            <Brand size="text-lg" textClass="hidden sm:inline-flex" iconClass="h-8 w-8" />
          )}
        </Link>

        {restaurant && (
          <button
            onClick={copyCode}
            data-testid="join-code-copy-button"
            className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] text-xs font-mono text-terra-600 hover:border-terra-400/50 transition-colors"
            title="Copy join code"
          >
            {restaurant.name} · {restaurant.join_code}
            <Copy className="w-3 h-3" />
          </button>
        )}

        <nav className="flex items-center gap-1 ml-auto">
          {isManager ? (
            <>
              <NavLink to="/manager" icon={<LayoutDashboard className="w-4 h-4" />} label="Board" testid="nav-dashboard-link" />
              <NavLink to="/manager/templates" icon={<ListChecks className="w-4 h-4" />} label="Templates" testid="nav-templates-link" />
              <NavLink to="/manager/schedule" icon={<CalendarDays className="w-4 h-4" />} label="Schedule" testid="nav-schedule-link" />
              <NavLink to="/manager/team" icon={<Users className="w-4 h-4" />} label="Team" testid="nav-team-link" />
              <NavLink to="/manager/settings" icon={<SettingsIcon className="w-4 h-4" />} label="Settings" testid="nav-settings-link" />
            </>
          ) : (
            <NavLink to="/app" icon={<ClipboardList className="w-4 h-4" />} label="My Shifts" testid="nav-shifts-link" />
          )}
        </nav>

        <div className="flex items-center gap-2 pl-2 border-l border-[#E4DDD3]">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium text-[#1A1D20] leading-tight" data-testid="topbar-user-name">{user?.name}</div>
            <div className="text-xs text-terra-600">{user?.primary_role}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:text-terra-600 hover:bg-[#FDF0EB] transition-colors"
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );
}
